import { type Organization } from "@packages/core";
import { useVirtualizer } from "@tanstack/react-virtual";
import React, { type ReactNode, useRef } from "react";
import { LuClipboardCopy } from "react-icons/lu";
import { type Merge } from "type-fest";
import { useTranslate } from "../../services";
import { css, cx } from "../../styled-system/css";
import {
  Tooltip,
  TypographyStyle,
  ellipsis,
  monospace,
  skeletonStyle,
  tableStyles,
} from "../common/index";
import { copyToClipboard } from "../utilities";

const classes = tableStyles.raw({ foot: false });
const rowStyle = css.raw({
  gridTemplateColumns: "104px minmax(120px, 1fr) 104px",
});

export type OrganizationTableProperties = Merge<
  React.ComponentPropsWithoutRef<"div">,
  {
    action?: { (organization: Organization): ReactNode };
    loading?: boolean;
    organizations?: ReadonlyArray<Organization>;
  }
>;

export function OrganizationTable({
  action,
  className,
  loading,
  organizations = [],
  ...properties
}: OrganizationTableProperties) {
  const translate = useTranslate();

  const parentRef = useRef<HTMLDivElement>(null);

  const virtualizer = useVirtualizer({
    count: organizations.length,
    estimateSize() {
      return 64;
    },
    getScrollElement() {
      return parentRef.current;
    },
    overscan: 8,
  });

  return (
    <div {...properties} className={cx(css(classes.root), className)}>
      <div className={css(classes.head)}>
        <div className={css(classes.headRow, rowStyle)}>
          <div
            className={TypographyStyle({
              color: "primary/60",
              ellipsis: true,
              fontSize: "14px",
              fontWeight: 500,
              lineHeight: 1,
            })}
          >
            {translate("%OrganizationKey.0%")}
          </div>
          <div
            className={TypographyStyle({
              color: "primary/60",
              ellipsis: true,
              fontSize: "14px",
              fontWeight: 500,
              lineHeight: 1,
            })}
          >
            {translate("%Name.0%")}
          </div>
          <div />
        </div>
      </div>

      <div className={css(classes.virtualOuterBody)} ref={parentRef}>
        <div
          className={css(classes.virtualInnerBody)}
          style={{ height: virtualizer.getTotalSize() + "px" }}
        >
          {virtualizer.getVirtualItems().map(function (virtualItem) {
            const organization = organizations[virtualItem.index];
            return (
              <div
                className={css(classes.virtualBodyRow, rowStyle)}
                data-index={virtualItem.index}
                key={virtualItem.key}
                ref={virtualizer.measureElement}
                style={{
                  height: virtualItem.size + "px",
                  transform: "translateY(" + virtualItem.start + "px)",
                }}
              >
                <div>
                  <Tooltip
                    content={
                      <div className={css({ display: "flex", alignItems: "center", gap: "8px" })}>
                        <LuClipboardCopy className={css({ height: "14px", width: "auto" })} />
                        <span
                          className={css(monospace, ellipsis, {
                            backgroundColor: "muted-background",
                          })}
                        >
                          {organization.organizationKey}
                        </span>
                      </div>
                    }
                    trigger={
                      <span
                        className={css(
                          TypographyStyle.raw({
                            ellipsis: true,
                            fontSize: "14px",
                            lineHeight: 1,
                            monospace: true,
                          }),
                          {
                            cursor: "pointer",
                            _hover: { fontWeight: 500, textDecoration: "underline" },
                            _active: { fontWeight: 700 },
                          },
                        )}
                        onClick={function () {
                          copyToClipboard(organization.organizationKey);
                        }}
                      >
                        {organization.organizationKey}
                      </span>
                    }
                  />
                </div>
                <div
                  className={TypographyStyle({ ellipsis: true, fontSize: "14px", lineHeight: 1 })}
                >
                  {organization.name}
                </div>
                <div>{action?.(organization)}</div>
              </div>
            );
          })}
        </div>

        {organizations.length === 0 &&
          (loading ? (
            Array.from({ length: 1 }).map(function (_, index) {
              return (
                <div className={css(classes.bodyRow, rowStyle, { padding: "16px" })} key={index}>
                  <div className={css(skeletonStyle, { maxWidth: "96px" })} />
                  <div className={css(skeletonStyle, { maxWidth: "128px" })} />
                  <div />
                </div>
              );
            })
          ) : (
            <div className={css(classes.bodyRow, { padding: "16px" })}>
              <p className={css({ fontWeight: 500 })}>{translate("%NoItems.0%")}</p>
            </div>
          ))}
      </div>
    </div>
  );
}
